import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, Eye, ArrowUp, ArrowDown, X } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import EmptyState from '../components/EmptyState';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatCurrency, formatDate, shortId } from '../lib/utils';
import { useEnvInfo } from '../hooks/useEnvInfo';

export default function SettlementsPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isProduction } = useEnvInfo();
  const [page, setPage] = useState(1);
  const [dateSort, setDateSort] = useState('desc');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const pageSize = 20;

  const q = useQuery({
    queryKey: ['settlements', { page, pageSize, dateSort, search, dateFrom, dateTo }],
    queryFn: async () =>
      (await api.get('/settlements', {
        params: {
          page, page_size: pageSize, sort: dateSort,
          q: search.trim() || undefined,
          from_date: dateFrom || undefined,
          to_date: dateTo || undefined,
        },
      })).data,
  });

  const hasFilters = !!(search.trim() || dateFrom || dateTo);
  const clearFilters = () => {
    setSearch(''); setDateFrom(''); setDateTo(''); setPage(1);
  };

  const toggleDateSort = () => {
    setDateSort((s) => (s === 'desc' ? 'asc' : 'desc'));
    setPage(1);
  };

  const simulateSett = useMutation({
    mutationFn: async () =>
      (await api.post('/simulator/settlement', { include_uncategorized: false })).data,
    onSuccess: () => {
      toast.success('Liquidación procesada');
      qc.invalidateQueries({ queryKey: ['settlements'] });
    },
    onError: () => toast.error('Error generando liquidación'),
  });

  const data = q.data || { items: [], total: 0 };
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div data-testid="settlements-page" className="animate-fade-slide-up">
      <PageHeader
        title={t('settlements.title')}
        subtitle={t('settlements.subtitle')}
        actions={
          <>
            <Button
              variant="secondary"
              size="default"
              onClick={() => qc.invalidateQueries({ queryKey: ['settlements'] })}
              data-testid="settlements-refresh"
            >
              <RefreshCw className="h-[14px] w-[14px]" /> {t('common.refresh')}
            </Button>
            {!isProduction && (
              <Button
                size="default"
                onClick={() => simulateSett.mutate()}
                disabled={simulateSett.isPending}
                data-testid="settlements-simulate-button"
              >
                {t('common.simulate')}
              </Button>
            )}
          </>
        }
      />

      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="settlements-filters">
        <Input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder={t('settlements.search_placeholder')}
          className="h-8 w-64 text-xs"
          data-testid="settlements-search-input"
        />
        <div className="flex items-center gap-2">
          <Label htmlFor="settlements-date-from" className="text-xs text-muted-foreground whitespace-nowrap">
            {t('settlements.filter_from')}
          </Label>
          <Input
            id="settlements-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="h-8 w-40 text-xs"
            data-testid="settlements-date-from-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="settlements-date-to" className="text-xs text-muted-foreground whitespace-nowrap">
            {t('settlements.filter_to')}
          </Label>
          <Input
            id="settlements-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="h-8 w-40 text-xs"
            data-testid="settlements-date-to-input"
          />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8" onClick={clearFilters}
            data-testid="settlements-clear-filters">
            <X className="h-3.5 w-3.5 mr-1" />
            {t('settlements.filter_clear')}
          </Button>
        )}
      </div>

      <div className="premium-card overflow-hidden">
        {q.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-6">
            <EmptyState message={t('common.empty')} testid="settlements-empty" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="settlements-table">
              <TableHeader className="sticky top-0 z-10">
                <TableRow>
                  <TableHead>{t('settlements.column_id')}</TableHead>
                  <TableHead
                    onClick={toggleDateSort}
                    className="cursor-pointer select-none hover:text-foreground transition-colors"
                    data-testid="settlements-column-date-sort"
                    title={dateSort === 'desc' ? t('manual_review.sort_newest') : t('manual_review.sort_oldest')}
                  >
                    <span className="inline-flex items-center gap-1">
                      {t('settlements.column_date')}
                      {dateSort === 'desc'
                        ? <ArrowDown className="h-3 w-3" />
                        : <ArrowUp className="h-3 w-3" />}
                    </span>
                  </TableHead>
                  <TableHead className="text-right">{t('settlements.column_payments')}</TableHead>
                  <TableHead className="text-right">{t('settlements.column_fees')}</TableHead>
                  <TableHead className="text-right">{t('settlements.column_refunds')}</TableHead>
                  <TableHead className="text-right">{t('settlements.column_payout')}</TableHead>
                  <TableHead>{t('settlements.column_status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((s) => {
                  const fees = Object.values(s.fee_totals || {}).reduce((a, b) => a + b, 0);
                  return (
                    <TableRow
                      key={s.tiktok_settlement_id}
                      onClick={() => navigate(`/settlements/${s.tiktok_settlement_id}`)}
                      className="cursor-pointer"
                    >
                      <TableCell className="font-mono text-[13px] text-muted-foreground font-medium group-hover:text-[hsl(var(--primary))] transition-colors">
                        {shortId(s.tiktok_settlement_id, 14, 6)}
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground/80">
                        {formatDate(s.settlement_date, locale)}
                      </TableCell>
                      <TableCell className="text-right num text-foreground">
                        {formatCurrency(s.payments_total, locale)}
                      </TableCell>
                      <TableCell className="text-right num text-muted-foreground">
                        {formatCurrency(fees, locale)}
                      </TableCell>
                      <TableCell className="text-right num text-muted-foreground">
                        {formatCurrency(s.refunds_total, locale)}
                      </TableCell>
                      <TableCell className="text-right num font-bold text-foreground">
                        {formatCurrency(s.expected_payout, locale)}
                      </TableCell>
                      <TableCell><StatusBadge status={s.status || 'pending'} /></TableCell>
                      <TableCell className="text-right">
                        <button
                          type="button"
                          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-foreground/[0.05] transition-colors opacity-0 group-hover:opacity-100"
                          onClick={(e) => { e.stopPropagation(); navigate(`/settlements/${s.tiktok_settlement_id}`); }}
                          data-testid={`settlements-view-${s.tiktok_settlement_id}`}
                          aria-label="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-3 border-t border-default">
          <span className="text-[12px] text-muted-foreground">
            {t('common.page_of').replace('{{page}}', page).replace('{{pages}}', totalPages)}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm"
              disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              data-testid="settlements-pagination-prev">
              {t('common.pagination_prev')}
            </Button>
            <Button variant="secondary" size="sm"
              disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              data-testid="settlements-pagination-next">
              {t('common.pagination_next')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
